// hooks/useDesktopViolations.js
// Custom hook for polling desktop app violations in real-time

import { useState, useEffect, useRef, useCallback } from 'react';
import { isDesktopApp, getViolations } from '../utils/desktopBridge';

/**
 * Custom hook for polling desktop app cheating violations.
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether polling is active (default: true)
 * @param {number} options.pollInterval - Polling interval in ms (default: 5000)
 * @param {Function} options.onNewViolation - Callback when new violation detected
 * @returns {Object} - { violations, latestViolation, clearLatest, isDesktop }
 */
export function useDesktopViolations({
  enabled = true,
  pollInterval = 5000,
  onNewViolation = null
} = {}) {
  const [violations, setViolations] = useState([]);
  const [latestViolation, setLatestViolation] = useState(null);
  const seenViolationsRef = useRef(new Set());
  const isDesktop = isDesktopApp();

  // Clear the latest violation (for dismissing warnings)
  const clearLatest = useCallback(() => {
    setLatestViolation(null);
  }, []);

  // Get violation count by severity
  const getViolationStats = useCallback(() => {
    const stats = { critical: 0, high: 0, medium: 0, low: 0, total: violations.length };
    violations.forEach(v => {
      const severity = v.severity || 'medium';
      if (stats[severity] !== undefined) {
        stats[severity]++;
      }
    });
    return stats;
  }, [violations]);

  useEffect(() => {
    // Don't poll if not in desktop app or not enabled
    if (!isDesktop || !enabled) {
      console.log('[Desktop Violations] Polling disabled:', { isDesktop, enabled });
      return;
    }

    console.log('[Desktop Violations] Starting polling (interval:', pollInterval, 'ms)');

    const pollForViolations = async () => {
      try {
        const allViolations = await getViolations();
        console.log('[Desktop Violations] Fetched violations:', allViolations?.length || 0);
        if (!allViolations || allViolations.length === 0) return;

        // Find new violations we haven't seen yet
        const newViolations = allViolations.filter(v =>
          v.timestamp && !seenViolationsRef.current.has(v.timestamp)
        );

        if (newViolations.length > 0) {
          // Mark as seen
          newViolations.forEach(v => {
            seenViolationsRef.current.add(v.timestamp);
          });

          // Update state
          setViolations(prev => [...prev, ...newViolations]);

          // Set latest violation for warning display
          const mostRecent = newViolations[newViolations.length - 1];
          setLatestViolation(mostRecent);

          // Call callback if provided
          if (onNewViolation) {
            newViolations.forEach(v => onNewViolation(v));
          }

          // Log for debugging
          console.log('[Desktop Violations] New violations detected:', newViolations);
        }
      } catch (error) {
        console.error('[Desktop Violations] Error polling:', error);
      }
    };

    // Poll immediately on mount
    pollForViolations();

    // Set up interval
    const interval = setInterval(pollForViolations, pollInterval);

    return () => {
      console.log('[Desktop Violations] Stopping polling');
      clearInterval(interval);
    };
  }, [isDesktop, enabled, pollInterval, onNewViolation]);

  return {
    violations,
    latestViolation,
    clearLatest,
    isDesktop,
    getViolationStats
  };
}

export default useDesktopViolations;